import classNames from 'classnames';
import { Box, Flex, Inline } from '@atlaskit/primitives';
import Lozenge from '@atlaskit/lozenge';
import { CARD_PAYMENTS_SIDEBAR } from '../../../common/types';
import Button from '@atlaskit/button';
import { RequiredAsterisk } from '@atlaskit/form';
import { PaymentMethodId, PaymentMethodPanel } from '@atlassian/commerce-payment-methods';
import {
  errorNotificationBuilder,
  MoreActions,
  MoreActionsItem,
  NotificationTypes,
  OpenSidebarPayload,
  successNotificationBuilder,
  useNotification,
  useSidebar,
  WarningDialog,
} from '@css-xp/base';
import { CreditCard, PaymentMethod, PaymentMethodType } from '@css-xp/graphtypes/src/generated-types-new';
import { useQuery } from '@apollo/client';
import { setDefaultPaymentMethod } from '@atlassian/temp-proxies';
import { get } from 'lodash';
import React, { ReactElement, useState } from 'react';
import { failureMessage, getSupportUrl } from '@css-xp/ppc-common';
import { defaultBillingProfileSortOptions, maxThresholdForResults } from '@css-xp/ppc-constants';
import { fetchBillingProfilesByPaymentMethodIdQuery } from '@css-xp/billing-v2';
import { ADVOCATE_ACCESS_TYPE, ADVOCATE_RESOURCE_TYPE, useAdvocateAccessControl } from '@css-xp/ppc-access-control';
import { container, setAsDefaultButton } from './credit-card-payment.styles';

interface GetPaymentMethodOptionsArgs {
  paymentMethod: PaymentMethod;
  defaultPaymentMethodId: string | undefined | null;
  isOneColumnLayout?: boolean;
}

const sidebarOptions = {
  isOverlay: true,
  sidebarWidth: '600px',
};

export const getPaymentMethodOption = ({
  paymentMethod,
  defaultPaymentMethodId,
  isOneColumnLayout,
}: GetPaymentMethodOptionsArgs): ReactElement<typeof PaymentMethodPanel> | undefined => {
  if (paymentMethod.type === PaymentMethodType.CARD) {
    const creditCard = paymentMethod as CreditCard;

    // convert expMonth & expYear to Numbers
    const expMonth = +(creditCard.card?.expirationMonth ?? 0);
    const expYear = +(creditCard.card?.expirationYear ?? 0);
    const card = {
      brand: creditCard.card?.brand ?? '',
      expMonth,
      expYear,
      last4: creditCard.card?.last4Numbers ?? '',
      name: creditCard.card?.cardHolderName ?? '',
    };

    return (
      <div
        className={classNames('ppc-payment-method-panel-container', {
          'ppc-payment-method-panel-container-v2': isOneColumnLayout,
        })}
      >
        <PaymentMethodPanel
          paymentMethod={{
            id: creditCard.id as PaymentMethodId,
            type: paymentMethod.type,
            card,
            default: defaultPaymentMethodId === creditCard.id,
          }}
        />
      </div>
    );
  }
  return;
};

export const CreditCardPayment: React.FC<{
  txaId: string;
  card: PaymentMethod;
  onUpdateDone: () => void;
  isOneColumnLayout?: boolean;
}> = ({ txaId, card, onUpdateDone, isOneColumnLayout }) => {
  const { closeSidebar, openSidebar } = useSidebar();
  const isPaymentUpdateEnabledForAdvocates = useAdvocateAccessControl({
    accessType: ADVOCATE_ACCESS_TYPE.UPDATE,
    resource: ADVOCATE_RESOURCE_TYPE.PAYMENT_METHOD,
  });
  const isPaymentDeletionEnabledForAdvocates = useAdvocateAccessControl({
    accessType: ADVOCATE_ACCESS_TYPE.DELETE,
    resource: ADVOCATE_RESOURCE_TYPE.PAYMENT_METHOD,
  });
  const [isUpdating, setIsUpdating] = useState(false);
  const [isConfirmDialogOpen, setIsConfirmDialogOpen] = React.useState(false);
  const { sendSuccessNotification, notificationSent, setNotificationSent, sendErrorNotification } = useNotification();
  const paymentMethodId = card?.id || '';
  const { data, loading, error } = useQuery(fetchBillingProfilesByPaymentMethodIdQuery, {
    variables: {
      id: paymentMethodId,
      skip: !paymentMethodId,
      searchOptions: {
        first: maxThresholdForResults, // Disclaimer: Max threshold for the query
        offset: 0,
        sort: defaultBillingProfileSortOptions,
      },
    },
  });
  const billingProfiles = data?.billingProfilesByPaymentMethodId?.billingProfiles || [];
  const handleSidebarAction = (openSidebarPayload: OpenSidebarPayload) => {
    openSidebar(openSidebarPayload);
  };

  const handleSetAsDefaultClick = async () => {
    setIsUpdating(true);
    if (card && card.id) {
      try {
        await setDefaultPaymentMethod({
          transactionAccountId: txaId,
          id: card.id,
        });
        const notificationOptions = successNotificationBuilder()
          .isVersion(NotificationTypes.FLAG)
          .withId('update-default-payment-success')
          .withHeader(isOneColumnLayout ? 'Card set as default' : 'Update successful')
          .withMessage(
            isOneColumnLayout
              ? `Card **** **** **** ${get(
                  card,
                  'card.last4Numbers',
                  '****'
                )} set as your preferred card for future purchases.`
              : `Credit card **** **** **** ${get(card, 'card.last4Numbers', '****')} set as default payment method.`
          )
          .withDismissAfter(5000)
          .build();
        if (!notificationSent) {
          sendSuccessNotification(notificationOptions);
          setNotificationSent(true);
        }
        onUpdateDone();
      } catch (err) {
        const errorNotificationOptions = errorNotificationBuilder()
          .isVersion(NotificationTypes.FLAG)
          .withHeader('Update unsuccessful')
          .withMessage(<>We're unable to set the payment method - {failureMessage()}</>)
          .withId('update-default-payment-error')
          .withDismissAfter(5000)
          .build();

        if (!notificationSent) {
          sendErrorNotification(errorNotificationOptions);
          setNotificationSent(true);
        }
      } finally {
        setIsUpdating(false);
      }
    }
  };

  const getDescription = React.useCallback(() => {
    if (isOneColumnLayout) {
      return <div>Card **** **** **** {get(card, 'card.last4Numbers', '****')} will be used for future purchases.</div>;
    }

    return (
      <div className="payment-warning-description">
        <p className="pt-8">
          The new default payment method will be the preferred payment method for future purchases.
        </p>
        <p className="pt-8">If you need to change it for current subscriptions, go to the billing profile page.</p>
      </div>
    );
  }, [isOneColumnLayout, card]);

  const subHeader = (
    <p>We accept all major debit/credit cards. For help with other ways to pay, contact {getSupportUrl()}.</p>
  );

  const oneColumnSubHeader = (
    <>
      <p>
        Required fields are marked with an asterisk <RequiredAsterisk />
      </p>
      <br />
      <p>We accept all major debit/credit cards.</p>
    </>
  );

  const editPaymentMethodPayload = {
    txaId: txaId,
    paymentMethodId: card.id,
    onCancel: closeSidebar,
    onUpdateSuccess: closeSidebar,
    onUpdateFailure: closeSidebar,
    onUpdateDone,
    successMessage: isOneColumnLayout ? 'Card replaced' : 'Payment method replaced.',
    failureMessage: failureMessage(),
    showSubscriptionChargeNote: true,
    subHeader: isOneColumnLayout ? oneColumnSubHeader : subHeader,
    version: isOneColumnLayout ? 2 : undefined,
    isLoadingBillingProfiles: loading,
    billingProfiles,
  };

  const paymentMethodActions: MoreActionsItem[] = [
    ...(isPaymentUpdateEnabledForAdvocates && !isOneColumnLayout
      ? [
          {
            id: 'Set as default',
            content: 'Set as default',
            isDisabled: !!card.default,
            action: () => setIsConfirmDialogOpen(true),
          },
          {
            id: 'Replace',
            content: 'Replace',
            action: () =>
              handleSidebarAction({
                sidebarType: CARD_PAYMENTS_SIDEBAR.EDIT_PAYMENT_METHOD,
                payload: editPaymentMethodPayload,
                sidebarOptions,
              }),
          },
        ]
      : []),
    ...(isPaymentDeletionEnabledForAdvocates
      ? [
          {
            id: 'Remove',
            content: 'Remove',
            action: () =>
              handleSidebarAction({
                sidebarType: CARD_PAYMENTS_SIDEBAR.REMOVE_PAYMENT_METHOD,
                payload: {
                  refetch: onUpdateDone,
                  paymentMethod: card,
                  transactionAccountId: txaId,
                  version: isOneColumnLayout ? 2 : undefined,
                },
                sidebarOptions: isOneColumnLayout ? undefined : sidebarOptions,
              }),
          },
        ]
      : []),
    {
      id: 'View associated billing profiles',
      content: 'View associated billing profiles',
      action: () =>
        handleSidebarAction({
          sidebarType: CARD_PAYMENTS_SIDEBAR.VIEW_BILLING_PROFILE,
          payload: {
            paymentMethod: card,
            billingProfiles: billingProfiles,
            isLoading: loading,
            transactionAccountId: txaId,
            isFromBillingProfile: true,
            error: error,
          },
          sidebarOptions: sidebarOptions,
        }),
    },
  ];

  const renderWarningDialog = () => {
    return (
      <WarningDialog
        isOpen={isConfirmDialogOpen}
        onClose={() => setIsConfirmDialogOpen(false)}
        onConfirm={() => {
          setIsConfirmDialogOpen(false);
          handleSetAsDefaultClick();
        }}
        header={isOneColumnLayout ? 'Setting card as default' : 'This will be used for future purchases'}
        description={getDescription()}
        okLabel={'Confirm'}
        cancelLabel={'Close'}
        testId={'confirm-dialog-modal'}
        isIconRequired={false}
        okLabelAppearance="primary"
      />
    );
  };

  const renderMoreActions = () => <MoreActions actions={paymentMethodActions} testId="payment-method-actions-btn" />;

  if (isOneColumnLayout) {
    return (
      <Box xcss={container} padding="space.200" testId="ppc-payment-methods-line-item">
        <Flex justifyContent="space-between" alignItems="center">
          {getPaymentMethodOption({
            paymentMethod: card,
            defaultPaymentMethodId: null,
            isOneColumnLayout,
          })}
          <Flex alignItems="center">
            {card.default && <Lozenge>Default</Lozenge>}
            <Inline space="space.200">
              {isPaymentUpdateEnabledForAdvocates && (
                <Button
                  appearance="link"
                  onClick={() => {
                    handleSidebarAction({
                      sidebarType: CARD_PAYMENTS_SIDEBAR.EDIT_PAYMENT_METHOD,
                      payload: editPaymentMethodPayload,
                      sidebarOptions: {
                        sidebarTitle: 'Replace card',
                      },
                    });
                  }}
                  testId="replace-payment-method-btn"
                >
                  Replace
                </Button>
              )}
              {!card.default && isPaymentUpdateEnabledForAdvocates && (
                <Box xcss={setAsDefaultButton} paddingInlineStart="space.200">
                  <Button
                    appearance="link"
                    onClick={() => setIsConfirmDialogOpen(true)}
                    testId="set-as-default-payment-method-btn"
                  >
                    Set as default
                  </Button>
                </Box>
              )}
            </Inline>
            {renderMoreActions()}
          </Flex>
        </Flex>
        {renderWarningDialog()}
      </Box>
    );
  }

  return (
    <div
      key={`ppc-payment-method-${(card as any).last4Numbers}`}
      className="ppc-payment-method-line-item"
      data-testid="ppc-payment-methods-line-item"
    >
      <div className="card-details" data-testid={`payment-card-detail-${card.default ? 'default' : 'other'}`}>
        {getPaymentMethodOption({
          paymentMethod: card,
          defaultPaymentMethodId: null,
          isOneColumnLayout,
        })}
      </div>
      {
        <>
          {card.default && (
            <Button appearance="subtle" isDisabled>
              Default
            </Button>
          )}
          {renderMoreActions()}
        </>
      }
      {renderWarningDialog()}
    </div>
  );
};
